/*
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import * as React from 'react';
import styled from 'styled-components';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from '@web-stories-wp/react';
import { __ } from '@web-stories-wp/i18n';
import {
  Button,
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
  Icons,
  Input,
  Text,
  THEME_CONSTANTS,
} from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import { useStory } from '../../../app/story';
type StoryElement = {
  id: string;
  key?: string | null;
};

type StoryPage = {
  id: string;
  elements: StoryElement[];
};

type AutomationItem = {
  elementId: string;
  pageId: string;
  keyValue: string;
};

type UpdateElementsById = (args: {
  elementIds: string[];
  pageId?: string;
  properties: { key: string | null };
}) => void;

type SetCurrentPage = (args: { pageId: string }) => void;

type SetSelectedElementsById = (args: { elementIds: string[] }) => void;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 20px 24px;
  min-height: 100%;
`;

const AutomationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AutomationRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
`;

const StyledInput = styled(Input)`
  width: 100%;
`;

const HighlightButton = styled(Button)`
  svg {
    width: 22px;
    height: 22px;
  }
`;

const EmptyStateContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  text-align: center;
  min-height: 220px;
`;

const EmptyStateIcon = styled(Icons.NoKey)`
  width: 100px;
  height: 100px;
  color: ${({ theme }) => theme.colors.standard.white};
`;

const EmptyStateText = styled(Text).attrs({
  as: 'p',
  isBold: true,
  size: THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL,
})`
  color: ${({ theme }) => theme.colors.standard.white};
`;

function hasAutomationKey(value?: string | null): value is string {
  if (typeof value !== 'string') {
    return false;
  }
  return value.trim().length > 0;
}

function AutomateInspector(): JSX.Element {
  const { currentPage, updateElementsById, setCurrentPage, setSelectedElementsById } =
    useStory(
      ({
        state: { currentPage },
        actions: { updateElementsById, setCurrentPage, setSelectedElementsById },
      }: {
        state: { currentPage: StoryPage | null };
        actions: {
          updateElementsById: UpdateElementsById;
          setCurrentPage: SetCurrentPage;
          setSelectedElementsById: SetSelectedElementsById;
        };
      }) => ({
        currentPage,
        updateElementsById,
        setCurrentPage,
        setSelectedElementsById,
      })
    );

  const automationItems = useMemo<AutomationItem[]>(() => {
    if (!currentPage) {
      return [];
    }

    return currentPage.elements
      .filter((element: StoryElement) => hasAutomationKey(element.key ?? null))
      .map((element) => ({
        elementId: element.id,
        pageId: currentPage.id,
        keyValue: (element.key ?? '').trim(),
      }))
      .sort((a, b) => a.keyValue.localeCompare(b.keyValue, undefined, {
        sensitivity: 'base',
      }));
  }, [currentPage]);

  const [draftValues, setDraftValues] = useState<Record<string, string>>({});

  useEffect(() => {
    setDraftValues((prev) => {
      const next: Record<string, string> = {};

      automationItems.forEach((item) => {
        if (Object.prototype.hasOwnProperty.call(prev, item.elementId)) {
          next[item.elementId] =
            prev[item.elementId] !== item.keyValue
              ? item.keyValue
              : prev[item.elementId];
        } else {
          next[item.elementId] = item.keyValue;
        }
      });

      return next;
    });
  }, [automationItems]);

  const handleChange = useCallback((elementId: string) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setDraftValues((prev) => ({ ...prev, [elementId]: value }));
    };
  }, []);

  const handleBlur = useCallback(
    (item: AutomationItem) =>
      (event: React.FocusEvent<HTMLInputElement>) => {
        const { value } = event.target;
        const trimmedValue = value.trim();
        const normalizedValue = trimmedValue.length > 0 ? trimmedValue : null;
        const currentValue =
          item.keyValue.trim().length > 0 ? item.keyValue : null;

        if (normalizedValue === currentValue) {
          setDraftValues((prev) => {
            if (!normalizedValue) {
              const { [item.elementId]: _ignored, ...rest } = prev;
              return rest;
            }

            if (prev[item.elementId] === normalizedValue) {
              return prev;
            }

            return {
              ...prev,
              [item.elementId]: normalizedValue,
            };
          });
          return;
        }

        updateElementsById({
          elementIds: [item.elementId],
          pageId: item.pageId,
          properties: { key: normalizedValue },
        });

        setDraftValues((prev) => {
          if (normalizedValue === null) {
            const { [item.elementId]: _ignored, ...rest } = prev;
            return rest;
          }
          return {
            ...prev,
            [item.elementId]: normalizedValue,
          };
        });
      },
    [updateElementsById]
  );

  const handleHighlight = useCallback(
    (item: AutomationItem) => () => {
      setCurrentPage({ pageId: item.pageId });
      setSelectedElementsById({ elementIds: [item.elementId] });
    },
    [setCurrentPage, setSelectedElementsById]
  );

  return (
    <Container>
      {automationItems.length === 0 ? (
        <EmptyStateContainer>
          <EmptyStateIcon />
          <EmptyStateText>
            {__('No Automation Keys found', 'web-stories')}
          </EmptyStateText>
        </EmptyStateContainer>
      ) : (
        <AutomationList>
          {automationItems.map((item) => {
            const value = draftValues[item.elementId] ?? item.keyValue;
            return (
              <AutomationRow key={item.elementId}>
                <StyledInput
                  id={`automation-key-${item.elementId}`}
                  aria-label={__('Automation key', 'web-stories')}
                  value={value}
                  onChange={handleChange(item.elementId)}
                  onBlur={handleBlur(item)}
                  placeholder={__('Enter automation key', 'web-stories')}
                />
                <HighlightButton
                  variant={BUTTON_VARIANTS.CIRCLE}
                  type={BUTTON_TYPES.TERTIARY}
                  size={BUTTON_SIZES.SMALL}
                  aria-label={__('Highlight element', 'web-stories')}
                  onClick={handleHighlight(item)}
                >
                  <Icons.GoToElement />
                </HighlightButton>
              </AutomationRow>
            );
          })}
        </AutomationList>
      )}
    </Container>
  );
}

export default AutomateInspector;
